import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import QuickRegisterModal from "@/components/QuickRegisterModal";
import ReportContentButton from "@/components/ReportContentButton";
import PayoutManager from "@/components/PayoutManager";
import { QRCodeCanvas } from "qrcode.react";
import { Sparkle, Loader2, Play, Pause, PartyPopper, Gift, Wallet, Check, MessageCircle, Send, QrCode, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Category-Specific Pops — the AR Champagne Pop's confetti color AND shape now match the
// card's own vibe: gold sparkles for Regal, rainbow for Funny, stars for Celestial, etc.
const POP_THEMES = {
  regal: { colors: ["#f6e3a3", "#c9a24a", "#8a6a2a", "#d4af37"], shape: "circle" },
  holiday: { colors: ["#c9a24a", "#8a1f2b", "#1f4d34", "#f6e3a3"], shape: "circle" },
  funny: { colors: ["#ff5c8a", "#ffd23f", "#3fd9ff", "#7bd389", "#c77dff"], shape: "circle" },
  seasonal: { colors: ["#d97b3f", "#e6c15c", "#8a9a5b", "#c9a24a"], shape: "circle" },
  celestial: { colors: ["#c9a24a", "#8a6cae", "#4b3f8f", "#e0c3fc"], shape: "star" },
  default: { colors: ["#c9a24a", "#8a6cae", "#5a8c72", "#d4af37", "#b06a8e"], shape: "circle" },
};
const popTheme = (vibe) => POP_THEMES[vibe] || POP_THEMES.default;

const ExperienceHub = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [revealed, setRevealed] = useState(false); // champagne + cash after audio ends
  // The Maison Wallet claim
  const [claiming, setClaiming] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claimError, setClaimError] = useState("");
  // Reciprocity Loop — the Maison Reply
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyName, setReplyName] = useState("");
  const [replyMsg, setReplyMsg] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replySent, setReplySent] = useState(false);
  const [newResident, setNewResident] = useState(false);
  const [sealCopied, setSealCopied] = useState(false);
  // 15-second voice note alternative to typing
  const [replyMode, setReplyMode] = useState("text"); // "text" | "voice"
  const [recordingReply, setRecordingReply] = useState(false);
  const [replyAudioB64, setReplyAudioB64] = useState("");
  const [replyAudioUrl, setReplyAudioUrl] = useState("");
  const replyMediaRecRef = useRef(null);
  const replyChunksRef = useRef([]);
  // Reply-to-Register — Quick Registration Gateway
  const [quickRegisterOpen, setQuickRegisterOpen] = useState(false);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const acRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/experience/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This experience link is invalid."));
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [token]);

  const drawWave = useCallback(() => {
    const canvas = canvasRef.current, analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const bins = analyser.frequencyBinCount;
    const arr = new Uint8Array(bins);
    const render = () => {
      analyser.getByteFrequencyData(arr);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const bars = 40, step = Math.floor(bins / bars), bw = canvas.width / bars;
      for (let i = 0; i < bars; i++) {
        const v = arr[i * step] / 255;
        const h = Math.max(4, v * canvas.height * 0.9);
        const y = (canvas.height - h) / 2;
        const g = ctx.createLinearGradient(0, canvas.height, 0, y);
        g.addColorStop(0, "#c9a24a"); g.addColorStop(1, "#8a6cae");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.roundRect(i * bw + bw * 0.15, y, bw * 0.7, h, 4); ctx.fill();
      }
      rafRef.current = requestAnimationFrame(render);
    };
    render();
  }, []);

  const beginExperience = async () => {
    setStarted(true);
    const el = audioRef.current;
    if (!el || !data?.audio) { setRevealed(true); return; }  // no audio → reveal straight away
    try {
      if (data.soundwave && !acRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ac = new AC();
        const src = ac.createMediaElementSource(el);
        const analyser = ac.createAnalyser(); analyser.fftSize = 128;
        src.connect(analyser); analyser.connect(ac.destination);
        acRef.current = ac; analyserRef.current = analyser;
      }
      if (acRef.current?.state === "suspended") await acRef.current.resume();
      await el.play();
    } catch (e) { console.error("Autoplay blocked (handled by button):", e); }
  };

  useEffect(() => {
    const el = audioRef.current; if (!el) return;
    const onPlay = () => { setPlaying(true); if (data?.soundwave) drawWave(); };
    const onPause = () => { setPlaying(false); if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    const onEnded = () => {
      setPlaying(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setRevealed(true); // AR Reveal after audio completes
      if (navigator.vibrate) navigator.vibrate([30, 50, 30, 50, 80]);
      if (data?.cash_gift && !data?.claimed) axios.post(`${API}/toast/${token}/claim`, {}).catch(() => {});
    };
    el.addEventListener("play", onPlay); el.addEventListener("pause", onPause); el.addEventListener("ended", onEnded);
    return () => { el.removeEventListener("play", onPlay); el.removeEventListener("pause", onPause); el.removeEventListener("ended", onEnded); };
  }, [data, token, drawWave]);

  const claimWallet = async () => {
    setClaiming(true); setClaimError("");
    try {
      const res = await axios.post(`${API}/experience/${token}/claim-wallet`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("gifted_token")}` },
      });
      setClaimResult(res.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setClaimError((d && typeof d === "object" ? d.message : d) || "Could not claim right now.");
    } finally { setClaiming(false); }
  };

  const submitReply = async (authToken) => {
    setReplySending(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const body = replyMode === "voice" && replyAudioB64
        ? { name: replyName || "A recipient", audio_base64: replyAudioB64, audio_mime: "audio/webm" }
        : { name: replyName || "A recipient", message: replyMsg };
      const res = await axios.post(`${API}/experience/${token}/reply`, body, { headers });
      setNewResident(!!res.data?.reply?.new_resident);
      setReplySent(true); setReplyMsg(""); setReplyAudioB64(""); setReplyAudioUrl("");
    } catch (e) { console.error("Maison Reply failed:", e); }
    finally { setReplySending(false); }
  };

  const sendReply = async () => {
    const hasContent = replyMode === "voice" ? !!replyAudioB64 : !!replyMsg.trim();
    if (!hasContent) return;
    // Reply-to-Register — not logged in? Gate behind Quick Registration before sending.
    if (!user) { setQuickRegisterOpen(true); return; }
    await submitReply(localStorage.getItem("gifted_token"));
  };

  const startReplyRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      replyChunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && replyChunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(replyChunksRef.current, { type: "audio/webm" });
        setReplyAudioUrl(URL.createObjectURL(blob));
        const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(blob); });
        setReplyAudioB64(b64);
      };
      replyMediaRecRef.current = mr; mr.start(); setRecordingReply(true);
      setTimeout(() => { if (replyMediaRecRef.current?.state === "recording") replyMediaRecRef.current.stop(); setRecordingReply(false); }, 15000);
    } catch (e) { console.error("Mic access denied:", e); }
  };
  const stopReplyRecording = () => { if (replyMediaRecRef.current?.state === "recording") replyMediaRecRef.current.stop(); setRecordingReply(false); };

  const onQuickRegistered = async () => {
    setQuickRegisterOpen(false);
    // Account Onboarding — submit the held reply now that they're authenticated, then jump
    // straight to the Maison Wallet (zero friction to their first purchase).
    await submitReply(localStorage.getItem("gifted_token"));
    navigate("/dashboard?welcome=1");
  };

  if (error) return <div className="min-h-screen flex items-center justify-center text-red-500 p-6" data-testid="experience-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Preparing your experience…</div>;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" data-testid="experience-page">
      {revealed && data.cash_gift && (() => {
        const theme = popTheme(data.vibe);
        return Array.from({ length: 40 }).map((_, i) => (
          theme.shape === "star" ? (
            <span key={i} className="confetti-star" data-testid="pop-confetti-star" style={{
              left: `${Math.random() * 100}%`, color: theme.colors[i % theme.colors.length],
              animationDuration: `${1.5 + Math.random() * 1.8}s`, animationDelay: `${Math.random() * 0.4}s`,
            }}>★</span>
          ) : (
            <span key={i} className="confetti" data-testid="pop-confetti" style={{
              left: `${Math.random() * 100}%`, background: theme.colors[i % theme.colors.length],
              animationDuration: `${1.5 + Math.random() * 1.8}s`, animationDelay: `${Math.random() * 0.4}s`,
            }} />
          )
        ));
      })()}

      <div className="w-full max-w-md surface rounded-[2rem] p-8 text-center relative z-10 animate-fade-up">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center"><Sparkle className="w-5 h-5 text-white" /></div>
          <span className="gifted-brand text-2xl">GIFTED</span>
        </div>
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-2">A Sovereign Keepsake</p>
        <h1 className="font-serif text-3xl mb-1">For {data.loved_one_name}</h1>
        <p className="text-muted-foreground mb-5">{data.occasion}</p>

        {data.image_base64 && (
          <div className="rounded-2xl overflow-hidden border border-border mb-5">
            <img src={`data:image/*;base64,${data.image_base64}`} alt="" className="w-full h-44 object-cover" />
          </div>
        )}

        {/* Step 1 — Audio First */}
        {!started ? (
          <button data-testid="experience-begin" onClick={beginExperience}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-2xl py-4 text-lg hover:opacity-90 flex items-center justify-center gap-2 gold-pulse-ring">
            <Play className="w-5 h-5" /> {data.audio ? "Play your message" : "Reveal your gift"}
          </button>
        ) : (
          <>
            {data.audio && (
              <>
                {data.soundwave && <canvas ref={canvasRef} width="380" height="80" data-testid="experience-soundwave" className="w-full h-[80px] mb-3" />}
                <button data-testid="experience-play" onClick={() => (playing ? audioRef.current.pause() : audioRef.current.play())}
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary text-white flex items-center justify-center mx-auto hover:scale-105 transition-transform mb-4">
                  {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
                </button>
              </>
            )}
          </>
        )}

        {/* Step 2 — AR Reveal (champagne + cash) after audio completes */}
        {revealed && data.cash_gift && (
          <div className="cash-reveal my-5" data-testid="experience-cash">
            <div className="text-6xl mb-2 cork-pop select-none">🍾</div>
            <div className="text-sm text-muted-foreground flex items-center justify-center gap-1"><Gift className="w-4 h-4 gold-text" /> A cash gift of</div>
            <div className="font-serif text-5xl gold-text mt-1">${Number(data.cash_gift.amount).toFixed(2)}</div>
            {data.sender_name && <div className="text-xs text-muted-foreground mt-1" data-testid="cash-gift-sender">from {data.sender_name}</div>}
          </div>
        )}

        {/* The Maison Wallet — a REAL Stripe-confirmed gift, claimable into an account.
            This is the ROI loop: recipients must create/log into an account to claim. */}
        {revealed && data.wallet_claimable && (
          <div className="surface rounded-2xl p-4 mb-5" data-testid="wallet-claim-section">
            {claimResult ? (
              <div data-testid="wallet-claim-success">
                <div className="text-secondary flex items-center justify-center gap-2 text-sm mb-4">
                  <Check className="w-4 h-4" /> ${Number(claimResult.amount).toFixed(2)} added to your Maison Wallet (balance ${Number(claimResult.wallet_balance).toFixed(2)})
                </div>
                {/* Claim screen — the Cash-Out choice. Never a dead end, never forced to
                    spend on the platform: cashing out is right here, equally prominent to
                    the on-platform spend option. */}
                <p className="text-xs text-muted-foreground text-center mb-3">Your money, your choice — cash it out to your own bank or card, or spend it in the Maison.</p>
                <PayoutManager compact balance={claimResult.wallet_balance} onCashedOut={(amt) => setClaimResult((r) => ({ ...r, wallet_balance: Math.max(0, r.wallet_balance - amt) }))} />
                <button onClick={() => navigate("/dashboard#wallet")} data-testid="go-spend-btn"
                  className="w-full mt-3 text-sm flex items-center justify-center gap-1.5 surface rounded-xl py-2.5 text-primary hover:border-primary/40">
                  <Sparkle className="w-4 h-4" /> Spend in the Maison instead
                </button>
              </div>
            ) : user ? (
              <>
                <p className="text-sm text-muted-foreground mb-2 flex items-center justify-center gap-1.5"><Wallet className="w-4 h-4 gold-text" /> This gift is real — claim it into your Maison Wallet.</p>
                <button data-testid="wallet-claim-btn" onClick={claimWallet} disabled={claiming}
                  className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-xl py-2.5 hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2">
                  {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4" /> Claim into my Maison Wallet</>}
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-2 flex items-center justify-center gap-1.5"><Wallet className="w-4 h-4 gold-text" /> This gift is real. Create a free account or log in to claim it.</p>
                <button data-testid="wallet-claim-login-btn" onClick={() => navigate(`/login?next=/experience/${token}`)}
                  className="w-full bg-gradient-to-r from-primary to-secondary text-white rounded-xl py-2.5 hover:opacity-90">
                  Create account &amp; claim
                </button>
              </>
            )}
            {claimError && <p className="text-xs text-red-400 mt-2 text-center">{claimError}</p>}
          </div>
        )}
        {revealed && data.wallet_claimed && !claimResult && (
          <div className="text-xs text-muted-foreground mb-5 flex items-center justify-center gap-1.5" data-testid="wallet-already-claimed"><Check className="w-3.5 h-3.5 text-secondary" /> This gift has already been claimed.</div>
        )}

        {data.message && (
          <div data-testid="card-message-block">
            <p className="font-serif italic text-lg mt-2 mb-1.5">&ldquo;{data.message}&rdquo;</p>
            <div className="flex justify-center mb-5"><ReportContentButton contentType="card_message" contentId={data.id || token} /></div>
          </div>
        )}

        {/* Capsule Invitation — teases the recipient that more sentiments may arrive later,
            building anticipation for future "pops" of the champagne, whether or not any
            updates exist yet. Matches the Playfair/gold-text Sovereign tone used by the
            Silver/Gold Seal and Digital Signature sections below. */}
        {revealed && (
          <div className="mb-5 flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground" data-testid="capsule-invitation">
            <Sparkle className="w-3 h-3 gold-text shrink-0" />
            <span className="italic gold-text normal-case tracking-normal font-serif text-sm">This is a Living Artifact. Future memories may be added by the Sender.</span>
          </div>
        )}

        {/* The Digital Sovereign Seal — the same QR "Key" that leads here (AR Pop + Maison
            Wallet claim), shown on the recipient's own view too so it can be saved/shared as a
            keepsake, exactly like the physical postcard's printed seal. */}
        {revealed && (
          <div className="surface rounded-2xl p-4 mb-5 flex flex-col items-center" data-testid="digital-sovereign-seal">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><QrCode className="w-3 h-3 gold-text" /> Your Sovereign Seal</div>
            <div className="bg-[#f7f2ea] p-2 rounded-xl">
              <QRCodeCanvas value={window.location.href} size={104} fgColor="#b08a3a" bgColor="#f7f2ea" level="H" includeMargin={false} data-testid="sovereign-seal-qr" />
            </div>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(window.location.href); setSealCopied(true); setTimeout(() => setSealCopied(false), 2000); }}
              data-testid="sovereign-seal-copy" className="text-[11px] text-primary hover:underline mt-1.5 flex items-center gap-1">
              {sealCopied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Save this Seal</>}
            </button>
          </div>
        )}

        {/* The Living Capsule — post-send voice/video/photo updates from the sender */}
        {revealed && data.capsule_updates?.length > 0 && (
          <div className="mt-4 mb-6 text-left" data-testid="capsule-feed">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 text-center">A Living Update</div>
            <div className="space-y-3">
              {data.capsule_updates.map((u) => (
                <div key={u.id} className="surface rounded-2xl p-3" data-testid="capsule-feed-item">
                  {u.caption && <p className="text-sm font-medium mb-2">{u.caption}</p>}
                  {u.kind === "voice" && <audio controls className="w-full" src={`data:${u.mime || "audio/webm"};base64,${u.content_base64}`} />}
                  {u.kind === "video" && <video controls className="w-full rounded-xl" src={`data:${u.mime || "video/webm"};base64,${u.content_base64}`} />}
                  {u.kind === "photo" && <img src={`data:${u.mime || "image/jpeg"};base64,${u.content_base64}`} alt="" className="w-full rounded-xl object-cover" />}
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="text-[11px] text-muted-foreground">{(u.created_at || "").slice(0, 10)}</div>
                    <ReportContentButton contentType="capsule_update" contentId={u.id} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3 — Legacy Feed: signatures + keepsake timeline */}
        {revealed && (data.signatures?.length > 0 || data.timeline?.length > 0) && (
          <div className="mt-4 text-left" data-testid="experience-legacy">
            {data.signatures?.length > 0 && (
              <>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 text-center">Signed with love by</div>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {data.signatures.map((sig, i) => (
                    <div key={`${sig.name}-${i}`} className="surface rounded-2xl p-3 text-center">
                      {sig.image_base64 && <img src={`data:image/png;base64,${sig.image_base64}`} alt="" className="h-9 mx-auto object-contain" />}
                      <div className="font-medium text-sm mt-1">{sig.name}</div>
                      {sig.note && <div className="text-xs text-muted-foreground italic">&ldquo;{sig.note}&rdquo;</div>}
                    </div>
                  ))}
                </div>
              </>
            )}
            {data.timeline?.length > 0 && (
              <>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 text-center">Keepsake Timeline</div>
                <div className="space-y-2">
                  {data.timeline.map((t, i) => (
                    <div key={`${t.occasion}-${t.date}-${i}`} className="flex items-center gap-3 surface rounded-2xl p-2.5">
                      {t.image_base64 && <img src={`data:image/jpeg;base64,${t.image_base64}`} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                      <div className="flex-1"><div className="text-sm font-medium">{t.occasion}</div><div className="text-xs text-muted-foreground">{t.date}{t.has_audio ? " · 🎙 voice" : ""}</div></div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Reciprocity Loop — the Maison Reply, directly from the digital card view */}
        {revealed && (
          <div className="mt-4 text-left" data-testid="maison-reply-section">
            {replySent ? (
              <div className="text-sm text-secondary flex items-center justify-center gap-1.5" data-testid="maison-reply-sent"><Check className="w-4 h-4" /> Your reply was sent — it lands in their world instantly.{newResident ? " Welcome to the Maison!" : ""}</div>
            ) : replyOpen ? (
              <div className="surface rounded-2xl p-3 space-y-2">
                <input value={replyName} onChange={(e) => setReplyName(e.target.value)} placeholder="Your name (optional)" data-testid="maison-reply-name"
                  className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
                <div className="flex gap-1.5 mb-1">
                  <button type="button" data-testid="maison-reply-mode-text" onClick={() => setReplyMode("text")}
                    className={`flex-1 text-xs rounded-lg py-1.5 border ${replyMode === "text" ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>Type a message</button>
                  <button type="button" data-testid="maison-reply-mode-voice" onClick={() => setReplyMode("voice")}
                    className={`flex-1 text-xs rounded-lg py-1.5 border ${replyMode === "voice" ? "bg-primary/15 text-primary border-primary/30" : "surface text-muted-foreground"}`}>Record voice note</button>
                </div>
                {replyMode === "text" ? (
                  <textarea value={replyMsg} onChange={(e) => setReplyMsg(e.target.value)} rows={3} placeholder="Reply with a note of your own…" data-testid="maison-reply-textarea"
                    className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40 resize-none" />
                ) : (
                  <div className="text-center py-2">
                    {replyAudioUrl && <audio controls src={replyAudioUrl} className="w-full mb-2" data-testid="maison-reply-audio-preview" />}
                    {!recordingReply ? (
                      <button type="button" data-testid="maison-reply-record-btn" onClick={startReplyRecording}
                        className="w-full text-sm flex items-center justify-center gap-1.5 surface rounded-lg py-2 hover:border-primary/40">🎙 Record 15s</button>
                    ) : (
                      <button type="button" data-testid="maison-reply-stop-btn" onClick={stopReplyRecording}
                        className="w-full text-sm flex items-center justify-center gap-1.5 bg-red-500/15 text-red-500 rounded-lg py-2 gold-pulse-ring">■ Stop recording</button>
                    )}
                  </div>
                )}
                <button data-testid="maison-reply-send" onClick={sendReply}
                  disabled={replySending || (replyMode === "text" ? !replyMsg.trim() : !replyAudioB64)}
                  className="w-full text-sm flex items-center justify-center gap-1.5 bg-gradient-to-r from-secondary to-primary text-white rounded-lg py-2 hover:opacity-90 disabled:opacity-60">
                  {replySending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Maison Reply</>}
                </button>
              </div>
            ) : (
              <button data-testid="maison-reply-open" onClick={() => setReplyOpen(true)}
                className="w-full text-sm flex items-center justify-center gap-1.5 surface rounded-xl py-2.5 text-primary hover:border-primary/40">
                <MessageCircle className="w-4 h-4" /> Maison Reply
              </button>
            )}
          </div>
        )}

        {data.audio && <audio ref={audioRef} src={`data:${data.audio.mime};base64,${data.audio.base64}`} crossOrigin="anonymous" preload="auto" data-testid="experience-audio" />}
      </div>
      <QuickRegisterModal open={quickRegisterOpen} onClose={() => setQuickRegisterOpen(false)} onRegistered={onQuickRegistered} />
    </div>
  );
};

export default ExperienceHub;
